import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Loader2, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Cover } from "@/components/Cover";

/* "Scan audiobook folders": re-reads the FileJump audiobook folders (picks up new files, even outside the music root), then re-flags books.
   Polls while the import runs; `result` holds what the last scan found until dismissed. */
const n = (c, word) => `${c} ${word}${c === 1 ? "" : "s"}`;

/* Summary toast: how many books were added / updated / gone and, for a FileJump scan, how many files that took. */
const announce = (r, books) => {
  if (!r) { toast.success("Audiobook folders scanned"); return; }
  const parts = [r.new_count ? n(r.new_count, "new audiobook") : null, r.books_changed ? `${n(r.books_changed, "audiobook")} updated` : null, r.books_removed ? `${r.books_removed} no longer found` : null].filter(Boolean);
  const files = r.files_added != null ? [r.files_added ? n(r.files_added, "file") + " added" : null, r.files_changed ? n(r.files_changed, "file") + " changed" : null].filter(Boolean) : [];
  const title = parts.length ? parts.join(", ") : r.imported ? "Audiobook folders scanned — nothing new or changed" : `Library re-checked — ${n(books ?? 0, "book")}, nothing new`;
  toast.success(title, { description: r.imported ? `${files.length ? files.join(", ") : "No file changes"} across ${n(r.files_seen ?? 0, "file")} in your audiobook folders` : undefined });
};

export const useAudiobookScan = (onDone) => {
  const [state, setState] = useState({ running: false, found: null, scanned: null, result: null });
  const timer = useRef(null);
  const finish = useRef(onDone);
  finish.current = onDone;
  const poll = async () => {
    try {
      const { data } = await api.get("/audiobooks/scan");
      setState({ running: data.running, found: data.found, scanned: data.scanned, result: data.result });
      if (data.running) { timer.current = setTimeout(poll, 2500); return; }
      if (data.error) toast.error(`FileJump scan stopped: ${data.error}`);
      else announce(data.result);
      finish.current?.();
    } catch { setState((s) => ({ ...s, running: false })); }
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    api.get("/audiobooks/scan").then(({ data }) => {
      setState({ running: data.running, found: data.found, scanned: data.scanned, result: data.result });
      if (data.running) timer.current = setTimeout(poll, 2500);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const start = async () => {
    setState((s) => ({ ...s, running: true, result: null }));
    try {
      const { data } = await api.post("/audiobooks/scan");
      if (data.running) { timer.current = setTimeout(poll, 1500); return; }
      setState({ running: false, found: null, scanned: null, result: data.result });
      announce(data.result, data.books);
      finish.current?.();
    } catch (e) { setState((s) => ({ ...s, running: false })); toast.error(formatApiError(e)); }
  };
  const dismiss = async () => {
    setState((s) => ({ ...s, result: null }));
    try { await api.delete("/audiobooks/scan"); } catch { /* cosmetic */ }
  };
  return { ...state, start, dismiss };
};

export const ScanAudiobooksButton = ({ scan, className = "", testid = "audiobooks-scan" }) => {
  const label = scan.running ? (scan.found ? `Scanning… ${scan.scanned || 0}/${scan.found}` : "Scanning folders…") : "Scan audiobook folders";
  return (
    <button onClick={scan.start} disabled={scan.running} data-testid={testid} data-running={scan.running ? 1 : 0} title="Look for new audiobook files in your FileJump audiobook folders" className={`flex h-9 items-center gap-2 rounded-full border border-zinc-300 bg-white px-4 text-xs font-medium hover:border-zinc-900 disabled:opacity-60 ${className}`}>
      {scan.running ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}{label}
    </button>
  );
};

const when = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

/* What the last scan found: new books (linked) plus file counts, until dismissed. */
export const ScanResults = ({ scan }) => {
  const r = scan.result;
  if (!r || scan.running) return null;
  const files = r.files_added != null ? ` · ${n(r.files_added, "new file")}${r.files_changed ? `, ${r.files_changed} changed` : ""} of ${r.files_seen ?? "?"} seen` : "";
  return (
    <section className="mb-6 rounded-xl border border-zinc-200 bg-white/70 p-4 animate-rise" data-testid="scan-results" data-count={r.new_count}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-zinc-900">{[r.new_count ? n(r.new_count, "new audiobook") : null, r.books_changed ? `${n(r.books_changed, "audiobook")} updated` : null, r.books_removed ? `${r.books_removed} no longer found` : null].filter(Boolean).join(", ") || "No new audiobooks"}</p>
          <p className="text-xs text-zinc-500" data-testid="scan-results-meta">{r.imported ? "FileJump folders scanned" : "Library re-checked"} {when(r.finished_at)}{files}{r.error ? ` · stopped: ${r.error}` : ""}</p>
        </div>
        <button onClick={scan.dismiss} data-testid="scan-results-dismiss" aria-label="Dismiss" className="text-zinc-400 hover:text-zinc-900"><X size={16} /></button>
      </div>
      {r.new_books?.length > 0 && (
        <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="scan-results-list">
          {r.new_books.map((b) => (
            <li key={b.id}>
              <Link to={`/audiobooks/${b.id}`} data-testid={`scan-result-${b.id}`} className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-white p-2 transition-colors hover:border-zinc-900">
                <Cover albumId={b.id} hasCover={b.has_cover} version={b.cover_version} className="h-10 w-10 shrink-0" rounded="rounded" iconSize={16} />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-zinc-900">{b.name}</span>
                  <span className="block truncate text-xs text-zinc-500">{b.artist}{b.song_count ? ` · ${b.song_count} file${b.song_count === 1 ? "" : "s"}` : ""}</span>
                </span>
              </Link>
            </li>
          ))}
          {r.new_count > r.new_books.length && <li className="flex items-center gap-2 px-2 text-xs text-zinc-500"><BookOpen size={13} />and {r.new_count - r.new_books.length} more</li>}
        </ul>
      )}
    </section>
  );
};
